import { useEffect, useState, type ReactNode, type RefObject } from "react";
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  Airplay,
  PictureInPicture2,
} from "lucide-react";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { savePlaybackSpeed } from "@/lib/playbackMemory";
import { useAirPlay } from "./useAirPlay";

const clock = (seconds: number) => {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const total = Math.floor(seconds),
    h = Math.floor(total / 3600),
    m = Math.floor(total / 60) % 60,
    s = total % 60;
  return `${h ? `${h}:${String(m).padStart(2, "0")}` : m}:${String(s).padStart(2, "0")}`;
};
/** Approved chrome drives the existing HTML media element, never a fixture clock.
 * Mount beside the actual video (after source resolution), not its loading view. */
export function PlayerTransport({
  videoRef,
  frameRef,
  live = false,
  casting,
  disabled = false,
}: {
  videoRef: RefObject<HTMLVideoElement | null>;
  frameRef: RefObject<HTMLDivElement | null>;
  live?: boolean;
  casting?: ReactNode;
  disabled?: boolean;
}) {
  const [state, setState] = useState({
    paused: true,
    time: 0,
    duration: 0,
    muted: false,
    volume: 1,
    rate: 1,
  });
  const [error, setError] = useState<string | null>(null);
  const airplay = useAirPlay(videoRef);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const update = () =>
      setState({
        paused: video.paused,
        time: video.currentTime,
        duration: Number.isFinite(video.duration) ? video.duration : 0,
        muted: video.muted,
        volume: video.volume,
        rate: video.playbackRate,
      });
    const events = [
      "loadedmetadata",
      "durationchange",
      "timeupdate",
      "seeking",
      "seeked",
      "play",
      "pause",
      "ended",
      "volumechange",
      "ratechange",
      "emptied",
    ];
    events.forEach((event) => video.addEventListener(event, update));
    update();
    return () =>
      events.forEach((event) => video.removeEventListener(event, update));
  }, [videoRef]);
  const play = async () => {
    const video = videoRef.current;
    if (!video) return;
    setError(null);
    try {
      if (video.paused) await video.play();
      else video.pause();
    } catch {
      setError("Playback could not start. Try again.");
    }
  };
  const fullscreen = async () => {
    setError(null);
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else if (frameRef.current?.requestFullscreen)
        await frameRef.current.requestFullscreen();
      else {
        const video = videoRef.current as
          (HTMLVideoElement & { webkitEnterFullscreen?: () => void }) | null;
        if (video?.webkitEnterFullscreen) video.webkitEnterFullscreen();
        else setError("Full screen is unavailable in this browser.");
      }
    } catch {
      setError("Full screen is unavailable in this browser.");
    }
  };
  return (
    <>
      {state.paused && !disabled ? (
        <button
          type="button"
          className="player-start"
          aria-label="Play video"
          onClick={() => void play()}
        >
          <Play size={26} fill="currentColor" aria-hidden />
        </button>
      ) : null}
      <div
        className="player-transport"
        role="group"
        aria-label="Playback controls"
      >
        <button
          type="button"
          aria-label={state.paused ? "Play" : "Pause"}
          disabled={disabled}
          onClick={() => void play()}
        >
          {state.paused ? <Play aria-hidden /> : <Pause aria-hidden />}
        </button>
        <span className="player-time">
          {live ? "LIVE" : `${clock(state.time)} / ${clock(state.duration)}`}
        </span>
        {!live ? (
          <input
            className="tktic-range player-seek"
            type="range"
            aria-label="Playback position"
            min={0}
            max={state.duration || 1}
            step={1}
            value={Math.min(state.time, state.duration || 1)}
            disabled={disabled || !state.duration}
            aria-valuetext={`${clock(state.time)} of ${clock(state.duration)}`}
            onChange={(e) => {
              const time = Number(e.target.value);
              if (videoRef.current) {
                videoRef.current.currentTime = time;
                setState((current) => ({ ...current, time }));
              }
            }}
          />
        ) : (
          <span className="player-live-spacer" />
        )}
        <div className="player-volume">
          <button
            type="button"
            aria-label={state.muted ? "Unmute" : "Mute"}
            onClick={() => {
              if (videoRef.current) videoRef.current.muted = !state.muted;
            }}
          >
            {state.muted ? <VolumeX aria-hidden /> : <Volume2 aria-hidden />}
          </button>
          <input
            className="tktic-range"
            type="range"
            aria-label="Volume"
            min={0}
            max={1}
            step={0.05}
            value={state.muted ? 0 : state.volume}
            onChange={(e) => {
              const v = videoRef.current;
              if (v) {
                v.volume = Number(e.target.value);
                v.muted = false;
              }
            }}
          />
        </div>
        {!live ? (
          <TkticSelect
            ariaLabel="Playback speed"
            value={String(state.rate)}
            onChange={(value) => {
              if (videoRef.current) {
                videoRef.current.playbackRate = Number(value);
                savePlaybackSpeed(Number(value));
              }
            }}
            options={Array.from(
              new Set([0.25, 0.5, 0.75, 1, 1.25, 1.5, 2, state.rate]),
            )
              .sort((a, b) => a - b)
              .map((rate) => ({
                value: String(rate),
                label: `${rate}×`,
              }))}
          />
        ) : null}
        {airplay.available ? (
          <button
            type="button"
            aria-label="AirPlay"
            aria-pressed={airplay.casting}
            onClick={airplay.prompt}
          >
            <Airplay aria-hidden />
          </button>
        ) : null}
        {casting}
        {document.pictureInPictureEnabled && !live ? (
          <button
            type="button"
            aria-label="Picture in picture"
            onClick={async () => {
              try {
                if (document.pictureInPictureElement)
                  await document.exitPictureInPicture();
                else await videoRef.current?.requestPictureInPicture();
              } catch {
                setError(
                  "Picture in picture is unavailable for this recording.",
                );
              }
            }}
          >
            <PictureInPicture2 aria-hidden />
          </button>
        ) : null}
        <button
          type="button"
          aria-label="Full screen"
          onClick={() => void fullscreen()}
        >
          <Maximize aria-hidden />
        </button>
      </div>
      {error ? (
        <p role="alert" className="player-transport-error">
          {error}
        </p>
      ) : null}
    </>
  );
}
